import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, message, Modal, Radio, Row, Space, type FormInstance } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import icon from "../../resource/icon";
import Text from "../../components/Text/Text";
import Input from "../../components/Input/Input";
import Select from "../../components/Select/Select";
import CustomPersonInputList from "./CustomPersonInputList";
import { CardIndex, iconStyle, StyledCollapse, StyledTable, Wrapper } from "./StyleContent";
import {
  soApproveEntities_INIT,
  type soApproveEntities,
  type sequencesEntities,
} from "../../entities/SoApproveEntity";
import { color } from "../../resource";
import { roleDatasource } from "../../datasource/RoleDatasource";
import { NumberNotZero } from "../../utility/validator";
import { FieldData } from "rc-field-form/lib/interface";
import { deleteSequence } from "../../datasource/OverCreditApproveDatasource";

interface Props {
  data: soApproveEntities[];
  callBackUpdate: (data: soApproveEntities[]) => void;
  callErrForm: (err: boolean) => void;
  handleDelete: (id: string) => void;
  handleEdit: (item: soApproveEntities) => void;
}

const AmountOrderContent: React.FC<Props> = ({
  data,
  callBackUpdate,
  callErrForm,
  handleDelete,
  handleEdit,
}) => {
  const [formTable] = Form.useForm<{ groups: soApproveEntities[] }>();
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const updateBy = `${profile.firstname} ${profile.lastname}`;
  const [IndexGroup, setIndexGroup] = useState<string>("");
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [allGroups, setAllGroups] = useState<soApproveEntities[]>([soApproveEntities_INIT]);
  const [closedKeys, setClosedKeys] = useState<Set<string>>(new Set());
  const [allModeApproversMap, setAllModeApproversMap] = useState<Record<string, any[]>>({});
  const [roleList, setRoleList] = useState<
    { label: string; value: string; key: string; count: number }[]
  >([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Record<number, React.Key[]>>({});
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [errApprover, setErrorApprover] = useState(false);

  useEffect(() => {
    setAllGroups(data);
  }, [data]);

  useEffect(() => {
    const update = async () => {
      formTable.setFieldsValue({ groups: allGroups });
      const keysToExpand: Record<number, React.Key[]> = {};
      const roleCountsMap: Record<string, number> = {};
      for (let groupIndex = 0; groupIndex < allGroups.length; groupIndex++) {
        const group = allGroups[groupIndex];
        setIndexGroup(group?.soApproverGroupId);
        const seqKeys: React.Key[] = [];
        for (let seqIndex = 0; seqIndex < (group.sequences?.length || 0); seqIndex++) {
          const sequence = group.sequences[seqIndex];
          const countRole = await roleDatasource.userInRole(
            profile.company,
            sequence.rolename,
            "ACTIVE",
          );

          const roleUserCount = Array.isArray(countRole) ? countRole.length : 0;
          roleCountsMap[`${groupIndex}-${seqIndex}`] = roleUserCount;
          if (sequence.condition === "ASSIGN") {
            seqKeys.push(seqIndex);
          }
        }

        keysToExpand[groupIndex] = seqKeys;
      }

      setRoleCounts(roleCountsMap);
      setExpandedRowKeys(keysToExpand);
    };

    update();
  }, [allGroups, formTable, profile.company]);
  type SequenceFieldKey = keyof sequencesEntities;

  const handleCollapseChange = (keys: string | string[]) => {
    const newKeys = Array.isArray(keys) ? keys : [keys];
    const closed = activeKeys.filter((key) => !newKeys.includes(key));
    setClosedKeys((prev) => {
      const newClosed = new Set(prev);
      closed.forEach((key) => newClosed.add(key));
      newKeys.forEach((key) => newClosed.delete(key));
      return newClosed;
    });

    setActiveKeys(newKeys);
  };

  useEffect(() => {
    const allKeys = allGroups.map((g) => g.soApproverGroupId.toString());
    const keysToOpen = allKeys.filter((key) => !closedKeys.has(key));
    const isDifferent =
      keysToOpen.length !== activeKeys.length ||
      keysToOpen.some((key) => !activeKeys.includes(key));

    if (isDifferent) {
      setActiveKeys(keysToOpen);
    }
  }, [allGroups, closedKeys]);

  const setFormSequenceFields = (
    groupIndex: number,
    sequenceIndex: number,
    fields: Partial<sequencesEntities>,
  ) => {
    const entries = Object.entries(fields).map(([key, value]) => ({
      name: ["groups", groupIndex, "sequences", sequenceIndex, key as SequenceFieldKey],
      value,
    })) as FieldData<{ groups: soApproveEntities[] }>[];
    formTable.setFields(entries);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await roleDatasource.getAllRoles({
          page: 1,
          take: 100,
          company: profile?.company,
        });
        if (res?.data?.data) {
          setRoleList(
            res.data.data.map((role: any) => ({
              label: role.rolename,
              value: role.roleId,
              key: role.roleId,
              count: role.countmember,
            })),
          );
        }
      } catch (error) {
        message.error("Failed to load roles");
      }
    };
    if (profile?.company) fetchRoles();
  }, [profile?.company]);

  const teamZone = useCallback(
    (team: string, zone: string | string[]) => (
      <Row>
        <Col span={6}>
          <Text fontSize={14} color='background4'>
            ทีม
          </Text>
          <br />
          <Text fontSize={16} fontWeight={700}>
            {team}
          </Text>
        </Col>
        <Col span={18}>
          <Text fontSize={14} color='background4'>
            เขต
          </Text>
          <br />
          <Text fontSize={16} fontWeight={700}>
            {Array.isArray(zone) ? zone.join(", ") : zone}
          </Text>
        </Col>
      </Row>
    ),
    [],
  );

  const genExtra = useCallback(
    (id: string | undefined, item: soApproveEntities) => (
      <div style={{ display: "flex", gap: 16, marginRight: 22 }}>
        <img
          src={icon.iconEdit}
          alt='edit'
          style={iconStyle}
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(item);
          }}
        />
        <img
          src={icon.delete}
          alt='delete'
          style={iconStyle}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(id!);
          }}
        />
      </div>
    ),
    [handleDelete, handleEdit],
  );

  const handlePercentChange = (value: string, groupIndex: number, sequenceIndex: number) => {
    const percentNum = parseFloat(value);
    if (isNaN(percentNum)) return;

    const currentGroups = formTable.getFieldValue("groups") || [];
    const updatedGroups = [...currentGroups];
    const targetGroup = updatedGroups[groupIndex];
    if (!targetGroup) return;

    const sequences = [...(targetGroup.sequences || [])];
    const refPercent = parseFloat(sequences[1]?.amount ?? "0");
    if (sequenceIndex === 1) {
      if (sequences[0]) sequences[0].amount = value;
      sequences[1].amount = value;

      setFormSequenceFields(groupIndex, 0, { amount: value });
      setFormSequenceFields(groupIndex, 1, { amount: value });
    } else {
      if (percentNum < refPercent) {
        formTable.setFields([
          {
            name: ["groups", groupIndex, "sequences", sequenceIndex, "amount"],
            errors: [`เปอร์เซ็นต์ต้องไม่น้อยกว่า ${refPercent}%`],
          },
        ]);
        return;
      }

      sequences[sequenceIndex].amount = value;
      setFormSequenceFields(groupIndex, sequenceIndex, { amount: value });
    }

    updatedGroups[groupIndex].sequences = sequences;
    formTable.setFieldsValue({ groups: updatedGroups });

    const sequenceCount = sequences.length;
    for (let i = sequenceIndex + 1; i < sequenceCount; i++) {
      formTable.validateFields([["groups", groupIndex, "sequences", i, "amount"]]);
    }
  };

  const handleRole = useCallback(
    async (rolename: string, roleId: string, groupIndex: number, sequenceIndex: number) => {
      try {
        setAllGroups((prevGroups) => {
          const newGroups = [...prevGroups];
          const group = newGroups[groupIndex];
          if (!group) return prevGroups;

          const sequences = [...(group.sequences || [])];
          const sequence = sequences[sequenceIndex];
          if (!sequence) return prevGroups;

          sequences[sequenceIndex] = {
            ...sequence,
            approvers: [],
            rolename: "",
            minApprover: 1,
            condition: "",
            soApproverSequenceId: "",
          };
          newGroups[groupIndex] = { ...group, sequences };
          return newGroups;
        });

        setFormSequenceFields(groupIndex, sequenceIndex, {
          approvers: [],
          roleId: undefined,
          rolename: undefined,
          minApprover: 1,
        });
        formTable.setFields([
          {
            name: ["groups", groupIndex, "sequences", sequenceIndex, "roleId"],
            errors: [],
          },
        ]);
        const users = await roleDatasource.userInRole(profile.company, rolename, "ACTIVE");
        if (!Array.isArray(users)) return;
        const sequenceId =
          allGroups?.[groupIndex]?.sequences?.[sequenceIndex]?.soApproverSequenceId;
        const mappedUsers = users.map((user, idx) => ({
          overCreditApproverId: undefined,
          sequenceId,
          roleId: user.roleId,
          rolename: user.role,
          userId: user.userStaffId,
          firstname: user.firstname,
          lastname: user.lastname,
          number: idx + 1,
        }));
        const currentGroup = formTable.getFieldValue(["groups", groupIndex]);
        const sequences: any[] = currentGroup?.sequences ?? [];
        const roleCountMap: Record<string, number> = {};
        const firstDuplicateIndex: Record<string, number> = {};

        sequences.forEach((seq, idx) => {
          if (seq.roleId) {
            roleCountMap[seq.roleId] = (roleCountMap[seq.roleId] || 0) + 1;
            if (!(seq.roleId in firstDuplicateIndex)) {
              firstDuplicateIndex[seq.roleId] = idx;
            }
          }
        });

        const errorFields = sequences.map((seq, idx) => {
          const errors: string[] = [];

          if (seq.roleId && roleCountMap[seq.roleId] > 1 && idx === sequenceIndex) {
            errors.push("บทบาทซ้ำกับลำดับอื่น");
          }

          if (idx === sequenceIndex && users.length === 0) {
            errors.push("ไม่มีผู้ใช้งานในบทบาทนี้");
          }

          return {
            name: ["groups", groupIndex, "sequences", idx, "roleId"],
            errors,
          };
        });

        formTable.setFields(errorFields as unknown as Parameters<typeof formTable.setFields>[0]);

        const roleUserCount = users.length;
        const roleCountsKey = `${groupIndex}-${sequenceIndex}`;
        setRoleCounts((prev) => ({ ...prev, [roleCountsKey]: roleUserCount }));

        setFormSequenceFields(groupIndex, sequenceIndex, {
          approvers: mappedUsers,
          roleId,
          rolename,
          minApprover: 1,
        });
      } catch (error) {
        console.error("Failed to fetch users in role:", error);
      }
    },
    [profile.company, formTable, setFormSequenceFields, allGroups, setRoleCounts],
  );

  const handleConditionChange = async (value: string, groupIndex: number, index: number) => {
    const approverPath: ["groups", number, "sequences", number, "approvers"] = [
      "groups",
      groupIndex,
      "sequences",
      index,
      "approvers",
    ];
    const sequenceId = allGroups[groupIndex]?.sequences?.[index]?.soApproverSequenceId;
    const key = `${groupIndex}-${index}`;

    const dataFromTable = formTable.getFieldsValue(true);

    const checkApprval =
      (dataFromTable?.groups[groupIndex]?.sequences[index]?.approval?.length || 0) ==
      dataFromTable?.groups[groupIndex]?.sequences[index]?.minApprover;

    if (value === "ALL" || checkApprval) {
      const prevApprovers = allModeApproversMap[key] || formTable.getFieldValue(approverPath) || [];
      const updatedApprovers = prevApprovers.map((u: any, i: number) => ({
        sequenceId,
        roleId: u.roleId,
        rolename: u.rolename,
        userId: u.userId,
        firstname: u.firstname,
        lastname: u.lastname,
        number: i + 1,
      }));
      formTable.setFieldValue(approverPath, updatedApprovers);

      setAllGroups((groups) => {
        const newGroups = [...groups];
        const sequences = [...(newGroups[groupIndex]?.sequences || [])];
        if (sequences[index])
          sequences[index] = { ...sequences[index], approvers: updatedApprovers };
        newGroups[groupIndex] = { ...newGroups[groupIndex], sequences };
        return newGroups;
      });
    } else {
      const currentApprovers = formTable.getFieldValue(approverPath) || [];
      setAllModeApproversMap((prev) => ({ ...prev, [key]: currentApprovers }));

      formTable.setFieldValue(approverPath, []);

      setAllGroups((groups) => {
        const newGroups = [...groups];
        const sequences = [...(newGroups[groupIndex]?.sequences || [])];
        if (sequences[index]) sequences[index] = { ...sequences[index], approvers: [] };
        newGroups[groupIndex] = { ...newGroups[groupIndex], sequences };
        return newGroups;
      });
    }

    setExpandedRowKeys((prev) => {
      const groupExpanded = prev[groupIndex] ?? [];
      return {
        ...prev,
        [groupIndex]:
          value === "ASSIGN"
            ? groupExpanded.includes(index)
              ? groupExpanded
              : [...groupExpanded, index]
            : groupExpanded.filter((i) => i !== index),
      };
    });
  };

  const expandedRowRender = useCallback(
    (record: any, index: number, groupIndex: number): React.ReactNode | null => {
      const sequenceData: any = formTable.getFieldValue(["groups", groupIndex, "sequences", index]);
      if (sequenceData?.condition !== "ASSIGN") return null;
      return (
        <CustomPersonInputList
          err={(e) => {
            setErrorApprover(e);
          }}
          approverCount={sequenceData?.minApprover ?? 3}
          roleName={sequenceData?.rolename}
          sequenceId={allGroups[groupIndex].sequences[index].soApproverSequenceId}
          overCreditApproverIds={sequenceData?.approvers ?? []}
          callBackDataUser={(users) => {
            setAllGroups((prevGroups) => {
              const newGroups = [...prevGroups];
              const group = { ...newGroups[groupIndex] };
              const sequences = [...group.sequences];
              const sequence = { ...sequences[index] };
              sequence.approvers = users;
              sequences[index] = sequence;
              group.sequences = sequences;
              newGroups[groupIndex] = group;

              return newGroups;
            });

            formTable.setFields([
              {
                name: ["groups", groupIndex, "sequences", index, "approvers"],
                value: users,
              },
            ]);
          }}
          formTable={formTable}
          groupIndex={groupIndex}
          index={index}
        />
      );
    },
    [formTable, allGroups],
  );

  useEffect(() => {
    const groups = formTable.getFieldValue("groups") || [];
    const allFieldNames = ["rolename", "amount", "minApprover", "condition", "approvers"];
    let hasFormErrors = false;
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const sequences = groups[groupIndex].sequences || [];
      for (let seqIndex = 0; seqIndex < sequences.length; seqIndex++) {
        for (const field of allFieldNames) {
          const errors = formTable.getFieldError([
            "groups",
            groupIndex,
            "sequences",
            seqIndex,
            field as any,
          ]);
          if (errors && errors.length > 0) {
            hasFormErrors = true;
            break;
          }
        }
        if (hasFormErrors) break;
      }
      if (hasFormErrors) break;
    }

    const isValid =
      allGroups.length > 0 &&
      allGroups.every((group) => {
        if (!group.sequences || group.sequences.length === 0) return false;

        return group.sequences.every((seq) => {
          if (!seq.amount) return false;
          if (!seq.rolename) return false;
          if (!seq.minApprover) return false;
          if (!seq.condition) return false;

          if (seq.condition === "ASSIGN") {
            if (!seq.approvers || seq.approvers.length === 0) return false;
            const userIdSet = new Set<string>();
            for (const approver of seq.approvers) {
              if (!approver.firstname || !approver.lastname || !approver.number) return false;
              if (userIdSet.has(approver.userId)) {
                return false;
              }
              userIdSet.add(approver.userId);
            }
            if (errApprover) return false;
          }

          return true;
        });
      });
    const finalValid = isValid && !hasFormErrors;
    callErrForm(finalValid);
  }, [allGroups, formTable]);

  useEffect(() => {
    callBackUpdate(allGroups);
  }, [allGroups]);

  return (
    <Form
      form={formTable}
      layout='vertical'
      onValuesChange={() => {
        const currentGroups = formTable.getFieldValue("groups") || [];
        const updatedGroups = [...currentGroups];
        setAllGroups(updatedGroups);
      }}
    >
      <Wrapper>
        <StyledCollapse
          accordion={false}
          activeKey={activeKeys}
          expandIconPosition='end'
          expandIcon={({ isActive }) => (
            <img
              src={icon.iconDown}
              alt='toggle'
              style={{
                width: 22,
                height: 18,
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          )}
          onChange={handleCollapseChange}
          items={allGroups.map((item, groupIndex) => {
            const isNotLast = groupIndex < allGroups.length - 1;
            return {
              key: item.soApproverGroupId.toString(),
              label: teamZone(item.teamName, item.zones),
              extra: genExtra(item.soApproverGroupId, item),
              children: (
                <Form.List name={["groups", groupIndex, "sequences"]}>
                  {(fields, { add, remove }) => (
                    <div>
                      <StyledTable
                        rowKey='key'
                        dataSource={fields}
                        pagination={false}
                        expandable={{
                          expandIconColumnIndex: -1,
                          expandedRowKeys: expandedRowKeys[groupIndex] || [],
                          onExpandedRowsChange: (keys) => {
                            setExpandedRowKeys((prev) => ({
                              ...prev,
                              [groupIndex]: [...keys],
                            }));
                          },
                          expandedRowRender: (record) =>
                            expandedRowRender(record, record.name, groupIndex),
                          expandIcon: () => null,
                        }}
                        columns={[
                          {
                            title: "ช่วง",
                            width: 84,
                            render: (_: any, __: any, index: number) => (
                              <CardIndex>{index + 1}</CardIndex>
                            ),
                          },
                          {
                            title: "ยอดรวมคำสั่งซื้อ",
                            dataIndex: "amount",
                            key: "amount",
                            width: 180,
                            render: (_: any, __: any, index: number) => {
                              const groups = formTable.getFieldValue("groups") || [];
                              const sequences = groups[groupIndex]?.sequences || [];
                              return (
                                <Form.Item noStyle shouldUpdate>
                                  {() => (
                                    <Row
                                      align='middle'
                                      justify='center'
                                      style={{ marginTop: 8, alignItems: "baseline" }}
                                    >
                                      <Col span={4}>
                                        <img
                                          src={index === 0 ? icon.LessEqual : icon.moreThan}
                                          alt={index === 0 ? "LessEqual" : "MoreThan"}
                                          style={{ width: 20, height: 20 }}
                                        />
                                      </Col>
                                      <Col span={20}>
                                        <Form.Item
                                          name={[index, "amount"]}
                                          rules={[
                                            {
                                              required: index !== 0,
                                              message: "กรุณาระบุยอดรวมคำสั่งซื้อ",
                                            },
                                            ...(index > 1
                                              ? [
                                                  {
                                                    validator: (_, value) => {
                                                      if (!value) return Promise.resolve();
                                                      const valNum = Number(value);
                                                      if (isNaN(valNum)) {
                                                        return Promise.reject(
                                                          "กรุณากรอกตัวเลขที่ถูกต้อง",
                                                        );
                                                      }

                                                      const prevPercent = Number(
                                                        sequences?.[index - 1]?.amount ?? 0,
                                                      );
                                                      if (valNum <= prevPercent) {
                                                        return Promise.reject(
                                                          `กรุณากรอกมากกว่า ${prevPercent} บาท`,
                                                        );
                                                      }

                                                      return Promise.resolve();
                                                    },
                                                  },
                                                ]
                                              : []),
                                          ]}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <Input
                                            autoComplete='off'
                                            disabled={index === 0}
                                            placeholder='ระบุ'
                                            suffix='บาท'
                                            onKeyDown={(e) => NumberNotZero(e, true)}
                                            onChange={(e) =>
                                              handlePercentChange(e.target.value, groupIndex, index)
                                            }
                                          />
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  )}
                                </Form.Item>
                              );
                            },
                          },
                          {
                            title: "บทบาทที่ต้องอนุมัติ",
                            dataIndex: "roleId",
                            key: "roleId",
                            render: (_: any, __: any, index: number) => (
                              <Form.Item
                                name={[index, "roleId"]}
                                rules={[{ required: true, message: "เลือกบทบาทที่ต้องอนุมัติ" }]}
                              >
                                <Select
                                  placeholder='เลือกบทบาทที่ต้องอนุมัติ'
                                  data={roleList}
                                  allowClear
                                  onChange={(value) => {
                                    const selected = roleList.find((item) => item.value === value);
                                    if (selected) {
                                      handleRole(selected.label, selected.key, groupIndex, index);
                                    } else {
                                      formTable.setFieldsValue({
                                        groups: formTable
                                          .getFieldValue("groups")
                                          .map((group: any, gIdx: number) => {
                                            if (gIdx !== groupIndex) return group;
                                            return {
                                              ...group,
                                              sequences: group.sequences.map(
                                                (seq: any, sIdx: number) => {
                                                  if (sIdx !== index) return seq;
                                                  return {
                                                    ...seq,
                                                    minApprover: 1,
                                                    roleId: undefined,
                                                    rolename: undefined,
                                                    approvers: [],
                                                    approval: [],
                                                  };
                                                },
                                              ),
                                            };
                                          }),
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            ),
                          },
                          {
                            title: "เงื่อนไขการส่งคำขอ",
                            dataIndex: "condition",
                            key: "condition",
                            width: 220,
                            render: (_: any, __: any, index: number) => {
                              const groups = formTable.getFieldValue("groups") || [];
                              const sequences = groups[groupIndex]?.sequences[index] || [];
                              return (
                                <Form.Item
                                  name={[index, "condition"]}
                                  rules={[{ required: true, message: "เลือกเงื่อนไขการส่งคำขอ" }]}
                                >
                                  <Radio.Group
                                    onChange={(e) => {
                                      handleConditionChange(e.target.value, groupIndex, index);
                                    }}
                                    disabled={sequences.roleId ? false : true}
                                  >
                                    <Radio value='ALL'>
                                      <Text fontFamily='Sarabun' fontSize={14}>
                                        ส่งให้ทุกคน
                                      </Text>
                                    </Radio>
                                    <Radio value='ASSIGN'>
                                      <Text fontFamily='Sarabun' fontSize={14}>
                                        ระบุคน
                                      </Text>
                                    </Radio>
                                  </Radio.Group>
                                </Form.Item>
                              );
                            },
                          },
                          {
                            title: "",
                            key: "action",
                            width: 50,
                            render: (_: any, __: any, index: number) => (
                              <MinusCircleOutlined
                                onClick={async () => {
                                  const groups = formTable.getFieldValue("groups") || [];
                                  const sequence = groups?.[groupIndex]?.sequences?.[index];
                                  const sequenceId = sequence?.soApproverSequenceId;
                                  if (index < 2) return;
                                  Modal.confirm({
                                    title: "ยืนยันการลบช่วงการอนุมัติ",
                                    content: "คุณแน่ใจหรือไม่ว่าต้องการลบช่วงนี้?",
                                    okText: "ยืนยัน",
                                    cancelText: "ยกเลิก",
                                    onOk: async () => {
                                      if (sequenceId) {
                                        try {
                                          await deleteSequence(sequenceId, updateBy);
                                          message.success("ลบลำดับสำเร็จ");
                                        } catch (error) {
                                          message.error("เกิดข้อผิดพลาดในการลบลำดับ");
                                          return;
                                        }
                                      }
                                      remove(index);
                                    },
                                  });
                                }}
                                style={{
                                  fontSize: 24,
                                  color: index < 2 ? "#ccc" : color.Grey,
                                  cursor: index < 2 ? "not-allowed" : "pointer",
                                }}
                              />
                            ),
                          },
                        ]}
                      />
                      <div
                        style={{
                          backgroundColor: "#fff",
                          padding: 12,
                          display: "flex",
                          justifyContent: "end",
                        }}
                      >
                        <Button
                          type='dashed'
                          icon={<PlusOutlined style={{ color: color.primary }} />}
                          onClick={() => {
                            add({
                              amount: "",
                              roleId: undefined,
                              rolename: "",
                              minApprover: undefined,
                              //condition: "",
                              approvers: [],
                              groupId: IndexGroup || undefined,
                            });
                          }}
                          style={{
                            width: "96%",
                            height: "45px",
                            borderColor: color.primary,
                          }}
                        >
                          <Text fontSize={16} color='primary'>
                            เพิ่มช่วงการอนุมัติ
                          </Text>
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.List>
              ),
              style: isNotLast ? { marginBottom: 50 } : undefined,
            };
          })}
        />
      </Wrapper>
    </Form>
  );
};

export default AmountOrderContent;
